import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Trophy, Sparkles, RotateCcw, Printer, ArrowRight, Flame, Heart } from "lucide-react";
import { getProgress, BADGES, resetProgress } from "@/lib/progress";
import { toast } from "sonner";

function ProgressRing({ value, max, color = "bg-coral" }) {
    const pct = Math.min(100, Math.round((value / max) * 100));
    return (
        <div className="relative">
            <div className="w-40 h-40 rounded-full border-2 border-ink bg-white flex items-center justify-center">
                <div className="text-center">
                    <p className="font-heading font-bold text-4xl">{value}</p>
                    <p className="text-xs uppercase tracking-widest text-ink/60">points</p>
                </div>
            </div>
            <div className={`absolute -bottom-2 -right-2 w-12 h-12 rounded-full border-2 border-ink ${color} flex items-center justify-center font-heading font-bold text-sm`}>
                {pct}%
            </div>
        </div>
    );
}

export default function Rewards() {
    const [progress, setProgress] = useState(() => getProgress());

    useEffect(() => {
        const handler = () => setProgress(getProgress());
        window.addEventListener("twomk-progress-updated", handler);
        window.addEventListener("storage", handler);
        return () => {
            window.removeEventListener("twomk-progress-updated", handler);
            window.removeEventListener("storage", handler);
        };
    }, []);

    function handleReset() {
        if (!confirm("Reset all your progress? This can't be undone.")) return;
        resetProgress();
        setProgress(getProgress());
        toast.success("Progress reset.");
    }

    const { points, deeds, daysActive, earnedBadges, nextBadge, recent } = progress;
    const nextTarget = nextBadge?.points || (earnedBadges[earnedBadges.length - 1]?.points || 25);

    return (
        <div data-testid="rewards-page">
            {/* HERO */}
            <section className="border-b-2 border-ink bg-sun/40">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-[auto_1fr] gap-10 items-center">
                    <ProgressRing value={points} max={nextTarget} color={nextBadge?.color || "bg-mint"} />
                    <div>
                        <span className="pill bg-white">
                            <Trophy className="w-3.5 h-3.5" strokeWidth={2.5} /> Rewards
                        </span>
                        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-3">
                            Your Servant's Heart
                        </h1>
                        <p className="text-lg text-ink/80 mt-2 max-w-2xl">
                            Every kindness you check off earns points and badges grounded in Scripture. Print a certificate when you cross 50 points.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <Link to="/lessons" className="btn-sticker bg-coral text-white text-sm" data-testid="rewards-go-lessons">
                                <Sparkles className="w-4 h-4 mr-2" strokeWidth={2.5} /> Find a kindness
                            </Link>
                            <button onClick={() => window.print()} className="btn-sticker bg-white text-ink text-sm" data-testid="rewards-print-btn">
                                <Printer className="w-4 h-4 mr-2" strokeWidth={2.5} /> Print certificate
                            </button>
                            <button onClick={handleReset} className="btn-sticker bg-white text-ink text-sm" data-testid="rewards-reset-btn">
                                <RotateCcw className="w-4 h-4 mr-2" strokeWidth={2.5} /> Reset
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 grid sm:grid-cols-3 gap-4">
                <Stat icon={Heart} color="bg-coral" label="Deeds completed" value={deeds} dark />
                <Stat icon={Flame} color="bg-sun" label="Active days" value={daysActive} />
                <Stat icon={Award} color="bg-mint" label="Badges earned" value={`${earnedBadges.length}/${BADGES.length}`} />
            </section>

            {/* BADGES */}
            <section className="max-w-6xl mx-auto px-5 sm:px-8 py-6">
                <h2 className="font-heading text-3xl font-bold tracking-tight mb-1">Badges</h2>
                <p className="text-ink/70 mb-6">Unlock new badges as you go the second mile.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {BADGES.map((b) => {
                        const earned = points >= b.points;
                        const progressPct = Math.min(100, Math.round((points / b.points) * 100));
                        return (
                            <div
                                key={b.id}
                                className={`card-sticker p-6 ${earned ? b.color : "bg-white opacity-95"}`}
                                data-testid={`badge-${b.id}`}
                            >
                                <div className="text-5xl mb-2">{b.emoji}</div>
                                <h3 className="font-heading text-xl font-bold">{b.title}</h3>
                                <p className="text-sm text-ink/60 font-bold uppercase tracking-widest">{b.verse}</p>
                                <p className="font-heading mt-3 mb-2 text-sm font-semibold">{points}/{b.points} pts</p>
                                <div className="h-2 rounded-full bg-cream border-2 border-ink overflow-hidden">
                                    <div
                                        className="h-full bg-coral transition-all"
                                        style={{ width: `${progressPct}%` }}
                                    />
                                </div>
                                {earned ? (
                                    <p className="mt-3 font-heading font-semibold text-sm flex items-center gap-1">
                                        <Trophy className="w-4 h-4" strokeWidth={2.5} /> Earned!
                                    </p>
                                ) : (
                                    <p className="mt-3 text-sm text-ink/60">{b.points - points} pts to go</p>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* CERTIFICATE (printable when earned) */}
            {points >= 50 && (
                <section className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
                    <div className="card-sticker bg-white p-10 sm:p-14 text-center print:shadow-none print:border" data-testid="certificate">
                        <p className="font-accent text-3xl text-coral">Servant's Heart Certificate</p>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2">Awarded to a 2nd Mile Kid</h2>
                        <p className="mt-4 text-lg text-ink/80">
                            For walking with Jesus, loving your neighbor, and going the second mile —
                            you've completed <strong>{deeds}</strong> kindness{deeds === 1 ? "" : "es"} and earned <strong>{points}</strong> points.
                        </p>
                        <p className="mt-6 italic text-ink/80">
                            "For even the Son of Man came not to be served but to serve, and to give his life as a ransom for many."<br />
                            — Mark 10:45 (ESV)
                        </p>
                        <p className="mt-8 font-accent text-2xl text-ink/70">
                            Issued {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                        </p>
                    </div>
                </section>
            )}

            {/* RECENT ACTIVITY */}
            <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-16">
                <h2 className="font-heading text-2xl font-bold tracking-tight mb-4">Recent kindnesses</h2>
                {recent.length === 0 ? (
                    <div className="card-sticker p-8 bg-white text-center" data-testid="empty-recent-deeds">
                        <p className="font-heading text-lg">No deeds yet. Tap a kindness on any lesson to start!</p>
                        <Link to="/lessons" className="btn-sticker bg-sun text-ink mt-4 inline-flex">
                            Browse lessons <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2.5} />
                        </Link>
                    </div>
                ) : (
                    <ul className="space-y-3">
                        {recent.map((r, i) => (
                            <li key={i} className="card-sticker p-4 bg-white flex items-center justify-between gap-3" data-testid={`recent-${i}`}>
                                <div className="min-w-0">
                                    <p className="font-heading font-semibold truncate">{r.activityTitle}</p>
                                    <p className="text-sm text-ink/60 truncate">
                                        From: <Link to={`/lessons/${r.lessonId}`} className="underline">{r.lessonTitle}</Link> · {r.theme}
                                    </p>
                                </div>
                                <span className="pill bg-sun whitespace-nowrap">+{r.points} pts</span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}

function Stat({ icon: Icon, color, label, value, dark }) {
    return (
        <div className="card-sticker bg-white p-5">
            <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl border-2 border-ink ${color} mb-3`}>
                <Icon className={`w-5 h-5 ${dark ? "text-white" : "text-ink"}`} strokeWidth={2.5} />
            </div>
            <p className="text-xs uppercase tracking-widest font-bold text-ink/60">{label}</p>
            <p className="font-heading text-3xl font-bold mt-1">{value}</p>
        </div>
    );
}
